import type { InvoiceDiscount, InvoiceLine } from '../types';

export type DiscountInput = {
  type: InvoiceDiscount['type'];
  value: number;
};

export type ComputeResult = {
  lines: InvoiceLine[];
  subtotal: number;
  discount?: InvoiceDiscount;
  vatAmount: number;
  total: number;
};

/**
 * Calcule les totaux d'une facture.
 * L'escompte (remise pour paiement anticipé) est appliqué sur le HT et réduit
 * proportionnellement la TVA de chaque ligne.
 */
export function computeFromLines(
  lines: Omit<InvoiceLine, 'id' | 'totalExVat'>[],
  discount?: DiscountInput,
  kind: 'INVOICE' | 'CREDIT_NOTE' = 'INVOICE',
): ComputeResult {
  const normalized =
    kind === 'CREDIT_NOTE'
      ? lines.map(l => ({
          ...l,
          quantity: Math.abs(l.quantity),
          unitPrice: -Math.abs(l.unitPrice),
        }))
      : lines;

  const withTotals = normalized.map((l, i) => ({
    ...l,
    id: `L${i}-${Math.random().toString(36).slice(2)}`,
    totalExVat: l.quantity * l.unitPrice,
  }));
  const subtotal = withTotals.reduce((s, l) => s + l.totalExVat, 0);

  const discountAmount = computeDiscountAmount(
    subtotal,
    kind === 'CREDIT_NOTE' ? undefined : discount,
  );
  const discountRatio = subtotal > 0 ? discountAmount / subtotal : 0;

  const vatAmount = withTotals.reduce(
    (s, l) => s + l.totalExVat * (1 - discountRatio) * l.vatRate,
    0,
  );
  const subtotalAfterDiscount = subtotal - discountAmount;

  return {
    lines: withTotals,
    subtotal,
    discount:
      discount && discountAmount > 0
        ? { type: discount.type, value: discount.value, amount: discountAmount }
        : undefined,
    vatAmount,
    total: subtotalAfterDiscount + vatAmount,
  };
}

function computeDiscountAmount(subtotal: number, discount?: DiscountInput): number {
  if (!discount || !discount.value || discount.value <= 0 || subtotal <= 0) return 0;
  const raw = discount.type === 'percentage' ? subtotal * (discount.value / 100) : discount.value;
  return Math.max(0, Math.min(raw, subtotal));
}

/**
 * Une ligne **sans calcul à montrer** (#1770, suivora-api#1324).
 *
 * L'escompte pour paiement anticipé arrive sans unité, avec une quantité de 1 et un prix unitaire
 * qui répète son montant : ce n'est ni un jour, ni une heure, ni un forfait — c'est une déduction.
 * Son libellé porte déjà le taux et la condition, et son montant est dans sa colonne.
 *
 * Afficher « 1 jour × −500 € » sur une remise, c'est le repli d'un temps où toutes les lignes
 * étaient des prestations. Les colonnes de quantité et de prix restent donc vides ici — un tiret
 * marque la place sans rien affirmer.
 */
export const hasNoUnitPriceBreakdown = (line: { unit?: string | null }): boolean =>
  !line.unit?.trim();

/** Une ligne qui **retranche** : remise, escompte, régularisation. */
export const isDeduction = (line: { totalExVat: number }): boolean => line.totalExVat < 0;
